import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { dataService } from "../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, ToggleButtonGroup, ToggleButton, InputAdornment, Button } from "@mui/material";
import { getAuth } from "firebase/auth";
import { X, ChevronDown, EllipsisVertical, ChevronLeft } from "lucide-react";
import CustomCheckbox from "../../Shared/Checkbox";

export default function CompanyCrudDialog({ dialogOpen, closeDialog, ...props }) {
  const classes = useStyles();
  const auth = getAuth();

  const [companyToEdit, setCompanyToEdit] = useState(null);

  const [allCurrencies, setAllCurrencies] = useState([]);

  const [generalError, setGeneralError] = useState(null);

  const [companyName, setCompanyName] = useState(null);
  const [companyNameError, setCompanyNameError] = useState(null);

  const [currency, setCurrency] = useState(null);
  const [currencyError, setCurrencyError] = useState(null);

  const [companyHidden, setCompanyHidden] = useState(false);

  // Dropdown
  const [openDropdown, setOpenDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 }); // Position of the dropdown, relative to the dropdown button
  const openDropdownButtonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Three-dot-menu
  const [openThreeDotMenu, setOpenThreeDotMenu] = useState(false);
  const [threeDotPosition, setThreeDotPosition] = useState({ top: 0, left: 0 }); // Position of the dropdown, relative to the dropdown button
  const openThreeDotMenuButtonRef = useRef(null);
  const threeDotMenuRef = useRef(null);

  // Transfer company
  const [transferCompany, setTransferCompany] = useState(false);
  const [subscribers, setSubscribers] = useState([]);

  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [subscriberError, setSubscriberError] = useState(null);

  const [transferUsers, setTransferUsers] = useState(false);

  // fetch all currencies
  useEffect(() => {
    dataService
      .getAllCurrencies()
      .then((response) => {
        if (response) {
          setAllCurrencies(response);
          if (!props.companyToEdit) {
            setCurrency(response[1]); // set default currency to euro
          } else {
            // find the currency of the company to edit
            var currency = response.find((currency) => currency.id === props.companyToEdit.currencyId);
            setCurrency(currency);
          }
        } else {
          setGeneralError("Failed to fetch currencies");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (props.companyToEdit) {
      setCompanyToEdit(props.companyToEdit);
      setCompanyName(props.companyToEdit.name);
      // setCurrency({ id: props.companyToEdit.currencyId });
    }
  }, [props.companyToEdit]);

  useEffect(() => {
    if (openDropdown && openDropdownButtonRef.current) {
      // Calculate the position of the dropdown relative to the viewport
      const rect = openDropdownButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // Position dropdown below the trigger
        left: rect.left + window.scrollX, // Align to the left of the trigger
      });
    }
  }, [openDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && openDropdown) {
        // check if target is not the button
        if (!openDropdownButtonRef.current.contains(event.target)) {
          setOpenDropdown(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  useEffect(() => {
    if (openThreeDotMenu && openThreeDotMenuButtonRef.current) {
      // Calculate the position of the dropdown relative to the viewport
      const rect = openThreeDotMenuButtonRef.current.getBoundingClientRect();
      setThreeDotPosition({
        top: rect.bottom + window.scrollY + 4, // Position dropdown below the trigger
        left: rect.left + window.scrollX, // Align to the right of the trigger
      });
    }
  }, [openThreeDotMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (threeDotMenuRef.current && !threeDotMenuRef.current.contains(event.target) && openThreeDotMenu) {
        // check if target is not the button
        if (!openThreeDotMenuButtonRef.current.contains(event.target)) {
          setOpenThreeDotMenu(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openThreeDotMenu]);

  const changeCompanyName = (e) => {
    setCompanyName(e.target.value);
    if (e.target.value.length > 0) {
      setCompanyNameError(null);
    }
  };

  const handleCurrencySelection = (currency) => {
    setCurrency(currency);
    setOpenDropdown(false);
  };

  const checkFields = () => {
    var disabled = false;
    if (companyName === "" || companyName === null || companyName === undefined) {
      disabled = true;
    }
    return disabled;
  };

  const validateFields = () => {
    var validationGood = true;
    if (companyName === "" || companyName === null || companyName === undefined) {
      validationGood = false;
      setCompanyNameError("First name is required!");
    }

    if (currency === null || currency === undefined) {
      validationGood = false;
      setCurrencyError("Currency is required!");
    }

    return validationGood;
  };

  const handleHideCompany = () => {
    setOpenThreeDotMenu(false);
    setGeneralError(null);
    dataService
      .hideCompanyFromUserIdByCompanyId(props.companyToEdit.id, props.Login.userid, !props.companyToEdit.isHidden)
      .then((response) => {
        if (response.success) {
          window.location.reload();
        } else {
          setGeneralError("Failed to hide company");
        }
      })
      .catch((error) => {
        if (error.status === 403) {
          setGeneralError("You do not have access to this page.");
        } else {
          setGeneralError("Failed to hide company");
        }
      });
  };

  const handleSetTransferCompany = (value) => {
    if (value === true && subscribers.length === 0) {
      getSubcribers();
    }
    setOpenThreeDotMenu(false);
    setTransferCompany(value);
  };

  const handleSubscriberSelection = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setOpenDropdown(false);
    setSubscriberError(null);
  };

  const getSubcribers = () => {
    dataService
      .getAllSubscribers()
      .then((response) => {
        if (response) {
          setSubscribers(response);
        } else {
          setGeneralError("Failed to fetch subscribers");
        }
      })
      .catch((error) => {
        if (error.status === 403) {
          setGeneralError("You do not have access to this page.");
        }
      });
  };

  const handleSaveCompany = () => {
    if (transferCompany) {
      if (selectedSubscriber === null) {
        setSubscriberError("Subscriber is required!");
        return;
      }
      if (companyToEdit.subscriberUniqueId === selectedSubscriber.uniqueId) {
        setGeneralError("You cannot transfer the company to the same subscriber.");
        return;
      }
      let transferCompanyObject = {
        companyId: companyToEdit.id,
        receivingSubscriberUniqueId: selectedSubscriber.uniqueId,
        transferUsers: transferUsers,
      };
      dataService
        .transferCompany(transferCompanyObject)
        .then((response) => {
          if (response.success) {
            window.location.reload();
          } else {
            setGeneralError(response.message);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else if (validateFields()) {
      if (props.companyToEdit) {
        var ModifyCompanyObject = {
          id: props.companyToEdit.id,
          name: companyName,
          currencyId: currency.id,
          updatedByUserId: props.userId,
          subscriberUniqueId: props.subscriberUniqueId,
        };

        dataService
          .modifyCompany(ModifyCompanyObject)
          .then((response) => {
            if (response.success) {
              window.location.reload();
            } else {
              setGeneralError(response.message);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        var CreateCompanyObject = {
          name: companyName,
          currencyId: currency.id,
          insertedByUserId: props.userId,
          subscriberUniqueId: props.subscriberUniqueId,
        };

        dataService
          .createCompany(CreateCompanyObject)
          .then((response) => {
            if (response.success) {
              window.location.reload();
            } else {
              setGeneralError(response.message);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  };

  const handleOpenThreeDotMenu = () => {
    setOpenThreeDotMenu(true);
  };

  const handleSetCompanyState = (companyId) => {
    dataService
      .checkSubscriptionCompanyLimitByCompanyId(companyId)
      .then((result) => {
        let companyLimitReached = result;

        if (companyLimitReached && !companyToEdit.isActive) {
          setOpenThreeDotMenu(false);
          setGeneralError(
            "Can not activate the company because you have reached the maximum number of companies in your subscription. Please contact your administrator to increase the company limit."
          );
        } else {
          dataService
            .setCompanyState(companyId, !companyToEdit.isActive)
            .then((response) => {
              window.location.reload();
            })
            .catch((error) => {
              setOpenThreeDotMenu(false);
              if (error.status === 403) {
                setGeneralError("Only subscribers with an enterprise-level subscription can disable companies.");
              } else {
                setGeneralError(companyToEdit.isActive ? "Failed to disable company" : "Failed to enable company");
              }
            });
        }
      })
      .catch((error) => {
        setGeneralError(companyToEdit.isActive ? "Failed to disable company" : "Failed to enable company");
      });
  };

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" onClose={closeDialog} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          {props.companyToEdit ? (transferCompany ? "Transfer company" : "Edit company") : "Create company"}
        </DialogTitle>
        <div className={classes.head__buttonContainer}>
          {companyToEdit && (
            <>
              <button ref={openThreeDotMenuButtonRef} onClick={() => handleOpenThreeDotMenu()} className={classes.dialog__threeDotButton}>
                <EllipsisVertical size={19} />
              </button>
              {ReactDOM.createPortal(
                <div
                  ref={threeDotMenuRef}
                  className={classes.threeDotMenuDropdownContainer}
                  style={{ display: openThreeDotMenu ? "block" : "none", top: threeDotPosition.top, left: threeDotPosition.left }}
                >
                  {/* check if companyToEdit.users has a user with id === props.Login.userid */}
                  {companyToEdit.users && companyToEdit.users.length > 0 && companyToEdit.users.some((user) => user.id === props.Login.userid) && (
                    <button className={classes.dropdown__menuItem} onClick={() => handleHideCompany()}>
                      {companyToEdit.isHidden ? "Unhide company" : "Hide company"}
                    </button>
                  )}
                  {props.Login.isTaplrAdmin && (
                    <button className={classes.dropdown__menuItem} onClick={() => handleSetTransferCompany(true)}>
                      Transfer company
                    </button>
                  )}
                  <button className={companyToEdit.isActive ? classes.dropdown__menuItem_negative : classes.dropdown__menuItem_positive} onClick={() => handleSetCompanyState(props.companyToEdit.id)}>
                    {companyToEdit.isActive ? "Disable" : "Enable"}
                  </button>
                </div>,
                document.body
              )}
            </>
          )}
          <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
            <X size={19} />
          </button>
        </div>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {!props.companyToEdit && <p className={classes.dialog__subtitle}>Fill out the details below to create your new company</p>}
          {generalError && (
            <div className={classes.errorContainer}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          {transferCompany ? (
            <div className={classes.form}>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>Subscriber*</label>
                  <p className={classes.inputError}>{subscriberError}</p>
                </div>
                <div className={classes.container}>
                  <button
                    ref={openDropdownButtonRef}
                    className={classes.dropdown__inputContainer}
                    style={{ borderColor: subscriberError ? "#D84949" : "#B8B8B8" }}
                    onClick={() => setOpenDropdown(!openDropdown)}
                  >
                    <input type="text" className={classes.input} placeholder="Select subscriber" value={selectedSubscriber && selectedSubscriber.name} readOnly />
                    <ChevronDown className={classes.input__icon} style={{ transform: `rotate(${openDropdown ? "180deg" : "0deg"})` }} />
                  </button>
                  {ReactDOM.createPortal(
                    <div ref={dropdownRef} className={classes.dropdownContainer} style={{ display: openDropdown ? "block" : "none", top: dropdownPosition.top, left: dropdownPosition.left }}>
                      {subscribers.map((subscriber) => (
                        <button key={subscriber.id} className={classes.dropdown__menuItem} onClick={() => handleSubscriberSelection(subscriber)}>
                          {subscriber.name}
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              </div>
              {/* <div className={classes.inputContainer}>
                                <CustomCheckbox checked={transferUsers} onChange={setTransferUsers} label="Transfer users" />
                            </div> */}
            </div>
          ) : (
            <div className={classes.form}>
              <div className={classes.inputContainer}>
                <label className={classes.labelText}>Company name *</label>
                <TextField
                  className={classes.textField}
                  type={"text"}
                  variant="outlined"
                  size="small"
                  onChange={(event) => changeCompanyName(event)}
                  value={companyName}
                  fullWidth={true}
                  error={companyNameError !== null}
                  helperText={companyNameError}
                ></TextField>
              </div>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>Currency*</label>
                  <p className={classes.inputError}>{currencyError}</p>
                </div>
                <div className={classes.container}>
                  <button
                    ref={openDropdownButtonRef}
                    className={classes.dropdown__inputContainer}
                    style={{ borderColor: currencyError ? "#D84949" : "#B8B8B8" }}
                    onClick={() => setOpenDropdown(!openDropdown)}
                  >
                    <input type="text" className={classes.input} value={currency ? currency.name + " " + currency.symbol : ""} readOnly />
                    <ChevronDown className={classes.input__icon} style={{ transform: `rotate(${openDropdown ? "180deg" : "0deg"})` }} />
                  </button>
                  {ReactDOM.createPortal(
                    <div ref={dropdownRef} className={classes.dropdownContainer} style={{ display: openDropdown ? "block" : "none", top: dropdownPosition.top, left: dropdownPosition.left }}>
                      {allCurrencies.map((currency) => (
                        <button key={currency.id} className={classes.dropdown__menuItem} onClick={() => handleCurrencySelection(currency)}>
                          {currency.name} {currency.symbol}
                        </button>
                      ))}
                    </div>,
                    document.body
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        {transferCompany ? (
          <button className={classes.backButton} onClick={() => setTransferCompany(false)}>
            <ChevronLeft className={classes.backButton__icon} />
            Back
          </button>
        ) : (
          <button className={classes.secondaryButton} onClick={closeDialog}>
            Cancel
          </button>
        )}

        <button className={`${classes.primaryButton} ${checkFields() ? classes.disabled : ""}`} disabled={checkFields()} onClick={() => handleSaveCompany()}>
          {props.companyToEdit ? (transferCompany ? "Transfer company" : "Save changes") : "Create company"}
        </button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "400px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    lineHeight: "100% !important",

    padding: "0px !important",
  },

  head__buttonContainer: {
    display: "flex",
    gap: "8px",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__threeDotButton: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",
    opacity: "0.8",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  toggleFilter: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    position: "relative",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#DCDCDC",
    borderRadius: "4px",

    padding: "1px",
    marginBottom: "16px",
  },

  toggleFilter__optionButton: {
    height: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",

    border: "none",
    borderRadius: "3px",

    color: "#0C0E29",
    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    zIndex: "1",

    "&:hover": {
      // backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
  },

  toggleFilter__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0",
  },

  toggleSelected: {
    width: "calc(50% - 1px)",
    height: "30px",
    position: "absolute",
    transition: "transform 0.2s",

    backgroundColor: "white",
    borderRadius: "3px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "8px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  form: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  inputContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  // Dropdown
  container: {
    position: "relative",
  },

  dropdown__inputContainer: {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",

    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    border: "1px solid",
    borderRadius: "4px",
    backgroundColor: "transparent",

    padding: "0px",

    "&:hover": {
      border: "1px solid #0C0E29",
      cursor: "pointer",
    },
  },

  input: {
    width: "100%",
    height: "40px",
    padding: "0 14px",
    paddingRight: "32px",
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    color: "#0C0E29",
    fontSize: "16px",
    textAlign: "left",

    fontFamily: "Lato, sans-serif",

    "&:hover": {
      cursor: "pointer",
    },
  },

  input__icon: {
    position: "absolute",
    right: "10px",
    width: "20px",
    height: "20px",
    color: "#0C0E29",
  },

  threeDotMenuDropdownContainer: {
    width: "fit-content",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    transform: "translate(calc(-100% + 25px), 0)",
    // top: 'calc(100% + 4px)',
    // left: '0',
    zIndex: "1400",
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",

    display: "flex",
    flexDirection: "column",

    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdownContainer: {
    width: "fit-content",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    // top: 'calc(100% + 4px)',
    // left: '0',
    zIndex: "1400",
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",

    display: "flex",
    flexDirection: "column",

    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdown__collapseButton: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    gap: "8px",
    justifyContent: "space-between",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "rgba(12, 14, 41, 0.6)",
    backgroundColor: "#FFFFFF",

    // no text wrapping
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  collapseButton__icon: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },

  dropdown__menuItem: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__menuItem_negative: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#D84949",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#FDF3F3",
      color: "#C73B3B",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__menuItem_positive: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#3AB037",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F3FBF2",
      color: "#2B9128",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  saveButton: {
    border: "1px solid #C3996B !important",
    background: "#C3996B !important",
    display: "flex",
    color: "white !important",
    justifyContent: "center",
    alignItems: "center",

    "&:hover": {
      background: "rgba(195, 153, 107, 0.85) !important",
    },
    "&:disabled": {
      background: "#bdb6ac !important",
      border: "1px solid grey !important",
      color: "white !important",
      cursor: "not-allowed",
    },
  },
  cancelButton: {
    border: "1px solid #C3996B !important",
    background: "white",
    display: "flex",
    color: "#C3996B !important",
    justifyContent: "center",
    alignItems: "center",
    "&:hover": {
      background: "rgba(195, 153, 107, 0.85) !important",
    },
  },

  primaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "#C3996B",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  disabled: {
    backgroundColor: "#DCDCDC",
    color: "#989898",
    cursor: "not-allowed",

    "&:hover": {
      backgroundColor: "#DCDCDC",
      color: "#989898",
      cursor: "not-allowed",
    },
  },

  secondaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "1px solid #C3996B",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#C3996B",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      border: "1px solid #a77d4f",
      cursor: "pointer",
    },
  },

  backButton: {
    display: "flex",
    alignItems: "center",
    gap: "4px",

    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "#0C0E29",
    opacity: ".8",

    border: "none",
    padding: "0px",

    backgroundColor: "transparent",

    transition: "opacity ease-in-out .1s",

    "&:hover": {
      opacity: "1",
      cursor: "pointer",
    },
  },

  backButton__icon: {
    height: "16px",
    width: "16px",

    strokeWidth: "1.4px",
  },
}));
